import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  completeNewPassword,
  getCurrentSession,
  getEmailFromSession,
  getUserIdFromSession,
  getValidIdToken,
  signIn as cognitoSignIn,
  signOut as cognitoSignOut,
  type AppSession,
  type PendingChallenge,
  type SignInResult,
} from '../lib/cognito';
import { clearCredentialsCache, getIdentityPoolCredentials } from '../lib/awsCredentials';
import { signedGet } from '../lib/api';
import { clearCache } from '../lib/queryCache';
import { clearAuthUser, Sentry, setAuthUser } from '../lib/sentry';
import { applyBranding, cacheBranding, fetchPublicBranding, getCachedBranding, type OrgBranding } from '../lib/branding';
import type { OrgMember, Organisation } from '../types';

const SELECTED_ORG_KEY = 'heymakr_selected_org';

function getPersistedOrgId(userId: string): string | null {
  try {
    const stored = localStorage.getItem(SELECTED_ORG_KEY);
    if (!stored) return null;
    const { uid, orgId } = JSON.parse(stored);
    return uid === userId ? orgId : null;
  } catch {
    return null;
  }
}

function persistSelectedOrg(userId: string, orgId: string | null) {
  if (orgId) {
    localStorage.setItem(SELECTED_ORG_KEY, JSON.stringify({ uid: userId, orgId }));
  } else {
    localStorage.removeItem(SELECTED_ORG_KEY);
  }
}

function clearSelectedOrg() {
  localStorage.removeItem(SELECTED_ORG_KEY);
}

export interface AuthUser {
  id: string;
  email: string;
  user_metadata: { full_name?: string };
}

export interface OrgOption {
  organisation: Organisation;
  membership: OrgMember;
}

interface MeResponse {
  orgs: OrgOption[];
  branding?: OrgBranding;
}

interface AuthState {
  user: AuthUser | null;
  session: AppSession | null;
  organisation: Organisation | null;
  membership: OrgMember | null;
  allOrganisations: OrgOption[];
  branding: OrgBranding | null;
  orgSelectionRequired: boolean;
  loading: boolean;
  dataLoaded: boolean;
  newPasswordRequired: boolean;
}

interface AuthContextType extends AuthState {
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  setNewPassword: (password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  refreshOrg: () => Promise<void>;
  switchOrg: (orgId: string) => void;
  hasMultipleAccessPaths: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    organisation: null,
    membership: null,
    allOrganisations: [],
    // Seed from the localStorage cache so context consumers (Logo, app name) render
    // the branded look immediately; refreshed from /v1/me on load.
    branding: getCachedBranding(),
    orgSelectionRequired: false,
    loading: true,
    dataLoaded: false,
    newPasswordRequired: false,
  });

  // Holds the pending Cognito challenge (session + username) while the user completes a
  // NEW_PASSWORD_REQUIRED step.
  const pendingChallengeRef = useRef<PendingChallenge | null>(null);
  const loadedUserIdRef = useRef<string | null>(null);
  // Set once GET /v1/me has delivered the authenticated copy of the theme, so the slower
  // public fetch never overwrites it. Both carry the same document, but /v1/me is the
  // authoritative read and must win any race.
  const brandingFromMeRef = useRef(false);

  // App access requires organisation membership. A user with exactly one org is auto-selected;
  // with several, we honour a persisted choice or ask them to pick; with none, they stay org-less
  // (no access) and are shown the no-access state downstream.
  const resolveOrgSelection = (
    userId: string,
    allOrganisations: OrgOption[],
  ) => {
    let selectedOrg: Organisation | null = null;
    let selectedMembership: OrgMember | null = null;
    let orgSelectionRequired = false;

    if (allOrganisations.length === 1) {
      selectedOrg = allOrganisations[0].organisation;
      selectedMembership = allOrganisations[0].membership;
    } else if (allOrganisations.length > 1) {
      const persistedOrgId = getPersistedOrgId(userId);
      const match = persistedOrgId
        ? allOrganisations.find(o => o.organisation.id === persistedOrgId)
        : undefined;
      if (match) {
        selectedOrg = match.organisation;
        selectedMembership = match.membership;
      } else {
        orgSelectionRequired = true;
      }
    }

    return { selectedOrg, selectedMembership, orgSelectionRequired };
  };

  const loadUserData = async (session: AppSession) => {
    const cognitoSub = getUserIdFromSession(session);
    const email = getEmailFromSession(session);
    const idToken = session.idToken;

    try {
      const credentials = await getIdentityPoolCredentials(idToken);
      const me = await signedGet<MeResponse>('/v1/me', credentials);

      // Tenant-wide branding (root org + all sub-companies). Apply + cache so it
      // sticks across reloads and is applied before the next /v1/me resolves.
      const branding: OrgBranding = me.branding ?? {};
      brandingFromMeRef.current = true;
      applyBranding(branding);
      cacheBranding(branding);

      const allOrganisations: OrgOption[] = me.orgs ?? [];
      // The current user's id is the INTERNAL public.users.id (from /v1/me), no longer the Cognito
      // sub — the two are now decoupled server-side. Every membership carries the same internal id,
      // so any org works; fall back to the sub only when the user has no memberships. This keeps all
      // client-side comparisons (m.user_id === user.id, owner.user_id === user.id) and any user_id
      // sent to the API on internal ids.
      const userId = allOrganisations[0]?.membership?.user_id ?? cognitoSub;
      const { selectedOrg, selectedMembership, orgSelectionRequired } = resolveOrgSelection(
        userId,
        allOrganisations,
      );

      loadedUserIdRef.current = userId;
      setAuthUser(userId, email, selectedOrg?.id ?? null, selectedMembership?.role ?? null);
      setState(prev => ({
        ...prev,
        user: { id: userId, email, user_metadata: {} },
        session,
        organisation: selectedOrg,
        membership: selectedMembership,
        allOrganisations,
        branding,
        orgSelectionRequired,
        loading: false,
        dataLoaded: true,
        newPasswordRequired: false,
      }));
    } catch (err) {
      console.error('[AuthContext] loadUserData failed:', err);
      Sentry.captureException(err, { tags: { context: 'load_user_data' } });
      const userId2 = getUserIdFromSession(session);
      const email2 = getEmailFromSession(session);
      setState(prev => ({
        ...prev,
        user: { id: userId2, email: email2, user_metadata: {} },
        session,
        loading: false,
        dataLoaded: false,
      }));
    }
  };

  useEffect(() => {
    getCurrentSession().then(session => {
      if (session) {
        loadUserData(session);
      } else {
        setState(prev => ({ ...prev, loading: false }));
      }
    });

    // Pre-login theme, fetched in parallel with the session probe: the login screens paint
    // before any credential exists, and the localStorage copy only covers browsers that
    // already signed in once (and goes stale when the owner pushes a new theme). Skipped
    // once /v1/me has landed — it carries the same document, authenticated.
    fetchPublicBranding().then(branding => {
      if (!branding || brandingFromMeRef.current) return;
      setState(prev => ({ ...prev, branding }));
    });
  }, []);

  // Advances the auth UI on the outcome of any sign-in / challenge step. Shared by signIn and
  // setNewPassword.
  const resolveSignInResult = async (result: SignInResult): Promise<{ error: string | null }> => {
    if (result.type === 'error') return { error: result.message };

    if (result.type === 'new_password_required') {
      pendingChallengeRef.current = result.challenge;
      setState(prev => ({ ...prev, newPasswordRequired: true }));
      return { error: null };
    }

    pendingChallengeRef.current = null;
    setState(prev => ({ ...prev, newPasswordRequired: false }));
    await loadUserData(result.session);
    return { error: null };
  };

  const signIn = async (email: string, password: string): Promise<{ error: string | null }> => {
    return resolveSignInResult(await cognitoSignIn(email, password));
  };

  const setNewPassword = async (password: string): Promise<{ error: string | null }> => {
    const challenge = pendingChallengeRef.current;
    if (!challenge) return { error: 'No pending authentication session' };
    return resolveSignInResult(await completeNewPassword(challenge, password));
  };

  const signOut = async () => {
    loadedUserIdRef.current = null;
    pendingChallengeRef.current = null;
    clearAuthUser();
    clearCredentialsCache();
    clearSelectedOrg();
    clearCache();
    // The theme is tenant-wide and readable without auth (GET /v1/public/branding), so it
    // outlives the session: resetting it here only flashed the default Heymakr look onto the
    // login screen the user lands on. Cache and CSS variables stay exactly as they are.
    brandingFromMeRef.current = false;
    await cognitoSignOut();
    setState(prev => ({
      user: null,
      session: null,
      organisation: null,
      membership: null,
      allOrganisations: [],
      branding: prev.branding,
      orgSelectionRequired: false,
      loading: false,
      dataLoaded: false,
      newPasswordRequired: false,
    }));
  };

  const refreshOrg = useCallback(async () => {
    if (!state.session) return;
    try {
      const idToken = await getValidIdToken();
      if (!idToken) return;
      const credentials = await getIdentityPoolCredentials(idToken);
      const me = await signedGet<MeResponse>('/v1/me', credentials);
      const allOrganisations: OrgOption[] = me.orgs ?? [];
      const currentOrgId = state.organisation?.id;
      let org: Organisation | null = null;
      let mem: OrgMember | null = null;
      if (currentOrgId) {
        const match = allOrganisations.find((o: OrgOption) => o.organisation.id === currentOrgId);
        if (match) { org = match.organisation; mem = match.membership; }
      }
      const branding: OrgBranding = me.branding ?? {};
      brandingFromMeRef.current = true;
      applyBranding(branding);
      cacheBranding(branding);
      setState(prev => ({ ...prev, allOrganisations, organisation: org, membership: mem, branding }));
    } catch (err) {
      Sentry.captureException(err, { tags: { context: 'refresh_org' } });
    }
  }, [state.session, state.organisation?.id]);

  const switchOrg = useCallback((orgId: string) => {
    const match = state.allOrganisations.find(o => o.organisation.id === orgId);
    if (!match || !state.user) return;
    clearCache();
    persistSelectedOrg(state.user.id, orgId);
    setAuthUser(state.user.id, state.user.email, orgId, match.membership.role);
    setState(prev => ({
      ...prev,
      organisation: match.organisation,
      membership: match.membership,
      orgSelectionRequired: false,
    }));
  }, [state.allOrganisations, state.user]);

  const hasMultipleAccessPaths = state.allOrganisations.length > 1;

  return (
    <AuthContext.Provider value={{
      ...state,
      signIn,
      setNewPassword,
      signOut,
      refreshOrg,
      switchOrg,
      hasMultipleAccessPaths,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
